import { Box, IconButton, useTheme } from '@mui/material';
import { memo, useCallback, useEffect, useMemo, useRef, useState, createRef, type FC, type RefObject } from 'react';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
import { ChevronSelectorVertical, InfoCircle, Trash04, Toggle01Left } from '@untitledui-pro/icons/line';
import { Toggle01Right } from '@untitledui-pro/icons/solid';
import clsx from 'clsx';
import { UMBetaBadge } from '@urbanmetrix/ui-kit';
import type { BlockMetadata, BlockParameters, SerializedBlock } from '@visualizer/types';
import CustomTooltip, { CustomTooltipOffsetProvider } from '@gui/components/generic/CustomTooltip';
import { BlockCatalog } from '@data/Simulations/pipeline/BlockCatalog';
import { PolygonDrawerManager } from '@rendering/DrawingModule/PolygonDrawerManager';
import { DEVELOPMENT_BLOCK_TYPES } from './DevelopmentBlockTypes';
import PipelineParametersSection from './BlockParameters/PipelineParametersSection';

export const PIPELINE_BLOCK_SPACING = 38;

type PipelineBlockListProps = {
    blocks: Array<SerializedBlock<BlockParameters>>;
    selectedBlockId: string | null;
    onSelect: (blockId: string) => void;
    onToggle: (blockId: string, event: React.MouseEvent<HTMLButtonElement>) => void;
    onDelete: (blockId: string, event: React.MouseEvent<HTMLButtonElement>) => void;
    onMove: (blockId: string, newIndex: number) => void;
    onDragStart?: () => void;
    scrollContainerRef?: RefObject<HTMLElement | null>;
    onSelectedBlockExtraHeightChange?: (extraHeight: number) => void;
};

const AUTO_SCROLL_THRESHOLD_PX = 50;
const AUTO_SCROLL_SPEED_PX = 5;
const EXPANDED_BLOCK_MARGIN_BOTTOM = 8;

const slotToY = (slotIndex: number) => slotIndex * PIPELINE_BLOCK_SPACING;

const clampY = (value: number, count: number, extraBottom = 0) => {
    if (count <= 1) {
        return 0;
    }
    const maxY = slotToY(count - 1) + Math.max(0, extraBottom);
    return Math.min(Math.max(0, value), maxY);
};

const isMouseEvent = (event: DraggableEvent): event is MouseEvent => 'clientY' in event;
const isTouchEvent = (event: DraggableEvent): event is TouchEvent => 'touches' in event;

const getPointerClientY = (event: DraggableEvent): number | null => {
    if (isMouseEvent(event)) {
        return event.clientY;
    }
    if (isTouchEvent(event) && event.touches.length > 0) {
        return event.touches[0]?.clientY ?? null;
    }
    return null;
};

const getCurrentOrder = (blocks: Array<SerializedBlock<BlockParameters>>, visualOrder: string[] | null): string[] => {
    const baseIds = blocks.map((b) => b.id);
    if (!visualOrder) return baseIds;
    const known = new Set(visualOrder);
    const full = [...visualOrder];
    for (const id of baseIds) {
        if (!known.has(id)) {
            full.push(id);
        }
    }
    return full;
};

const PipelineBlockList: FC<PipelineBlockListProps> = ({
    blocks,
    selectedBlockId,
    onSelect,
    onToggle,
    onDelete,
    onMove,
    onDragStart,
    scrollContainerRef,
    onSelectedBlockExtraHeightChange,
}) => {
    const theme = useTheme();
    const blockRefs = useRef<Map<string, RefObject<HTMLDivElement | null>>>(new Map());
    const [dragState, setDragState] = useState<{ id: string; y: number } | null>(null);
    const [visualOrder, setVisualOrder] = useState<string[] | null>(null);
    const [selectedBlockExtraHeight, setSelectedBlockExtraHeight] = useState(0);
    const autoScrollState = useRef<{ direction: -1 | 0 | 1; frame: number | null }>({
        direction: 0,
        frame: null,
    });
    const dragDistanceSinceStartRef = useRef(0);
    const hasTriggeredDragStartRef = useRef(false);
    const suppressNextClickRef = useRef(false);

    const stopAutoScroll = useCallback(() => {
        autoScrollState.current.direction = 0;
        if (autoScrollState.current.frame !== null) {
            cancelAnimationFrame(autoScrollState.current.frame);
            autoScrollState.current.frame = null;
        }
    }, []);

    const startAutoScroll = useCallback(
        (direction: -1 | 1) => {
            const container = scrollContainerRef?.current;
            if (!container) {
                stopAutoScroll();
                return;
            }
            autoScrollState.current.direction = direction;
            if (autoScrollState.current.frame !== null) {
                return;
            }
            const tick = () => {
                const node = scrollContainerRef?.current;
                if (!node || autoScrollState.current.direction === 0) {
                    autoScrollState.current.frame = null;
                    return;
                }
                node.scrollBy({ top: autoScrollState.current.direction * AUTO_SCROLL_SPEED_PX });
                autoScrollState.current.frame = requestAnimationFrame(tick);
            };

            autoScrollState.current.frame = requestAnimationFrame(tick);
        },
        [scrollContainerRef, stopAutoScroll]
    );

    const handleAutoScroll = useCallback(
        (event: DraggableEvent) => {
            const container = scrollContainerRef?.current;
            if (!container) {
                stopAutoScroll();
                return;
            }

            const pointerY = getPointerClientY(event);
            if (pointerY === null) {
                stopAutoScroll();
                return;
            }

            const containerRect = container.getBoundingClientRect();
            if (pointerY < containerRect.top + AUTO_SCROLL_THRESHOLD_PX) {
                startAutoScroll(-1);
            } else if (pointerY > containerRect.bottom - AUTO_SCROLL_THRESHOLD_PX) {
                startAutoScroll(1);
            } else {
                stopAutoScroll();
            }
        },
        [scrollContainerRef, startAutoScroll, stopAutoScroll]
    );

    useEffect(() => stopAutoScroll, [stopAutoScroll]);

    const orderedBlocks = useMemo(() => {
        const ids = getCurrentOrder(blocks, visualOrder);
        const blockMap = new Map(blocks.map((block) => [block.id, block]));
        return ids.map((id) => blockMap.get(id)).filter((block): block is (typeof blocks)[number] => Boolean(block));
    }, [blocks, visualOrder]);
    const orderedBlockIds = useMemo(() => orderedBlocks.map((block) => block.id), [orderedBlocks]);
    const selectedBlockIndex = useMemo(
        () => (selectedBlockId ? orderedBlockIds.indexOf(selectedBlockId) : -1),
        [orderedBlockIds, selectedBlockId]
    );
    const isDraggingAny = dragState !== null;
    const selectedBlockExtraOffset = selectedBlockExtraHeight > 0 ? selectedBlockExtraHeight + EXPANDED_BLOCK_MARGIN_BOTTOM : 0;

    const blockMetadataById = useMemo(() => {
        const entries: Array<[string, BlockMetadata | undefined]> = blocks.map((block) => [
            block.id,
            BlockCatalog.getBlockMetadata(block.type),
        ]);
        return new Map<string, BlockMetadata | undefined>(entries);
    }, [blocks]);
    const hasParamsById = useMemo(() => {
        const entries: Array<[string, boolean]> = blocks.map((block) => [block.id, PipelineParametersSection.hasParameters(block.type)]);
        return new Map<string, boolean>(entries);
    }, [blocks]);

    const ensureRef = useCallback((id: string): RefObject<HTMLDivElement | null> => {
        let nodeRef = blockRefs.current.get(id);
        if (!nodeRef) {
            nodeRef = createRef<HTMLDivElement>();
            blockRefs.current.set(id, nodeRef);
        }
        return nodeRef;
    }, []);

    const getPreDragExtraOffset = useCallback(
        (blockId: string) => {
            if (isDraggingAny || selectedBlockIndex < 0 || selectedBlockExtraOffset <= 0) {
                return 0;
            }
            const index = orderedBlockIds.indexOf(blockId);
            return index > selectedBlockIndex ? selectedBlockExtraOffset : 0;
        },
        [isDraggingAny, orderedBlockIds, selectedBlockExtraOffset, selectedBlockIndex]
    );

    const handleStart = (_: DraggableEvent, _data: DraggableData) => {
        const ids = getCurrentOrder(blocks, visualOrder);
        dragDistanceSinceStartRef.current = 0;
        hasTriggeredDragStartRef.current = false;
        setVisualOrder((prev) => prev ?? ids);
    };

    const handleDrag = (id: string) => (event: DraggableEvent, data: DraggableData) => {
        dragDistanceSinceStartRef.current += Math.abs(data.deltaY);
        if (!hasTriggeredDragStartRef.current && dragDistanceSinceStartRef.current >= 3) {
            onDragStart?.();
            hasTriggeredDragStartRef.current = true;
        }
        const preDragExtra = dragState ? 0 : getPreDragExtraOffset(id);
        const nextY = clampY(Math.round(data.y), blocks.length, preDragExtra);
        const normalizedY = Math.max(0, nextY - preDragExtra);
        setDragState((prev) => {
            if (prev && prev.id === id && prev.y === normalizedY) {
                return prev;
            }
            return { id, y: normalizedY };
        });
        const proposedIndex = Math.round(normalizedY / PIPELINE_BLOCK_SPACING);
        setVisualOrder((prev) => {
            const currentOrder = getCurrentOrder(blocks, prev ?? null);
            const currentIndex = currentOrder.indexOf(id);
            const filteredOrder = currentOrder.filter((blockId) => blockId !== id);
            const clampedIndex = Math.max(0, Math.min(proposedIndex, filteredOrder.length));
            if (currentIndex === clampedIndex) {
                return prev;
            }
            filteredOrder.splice(clampedIndex, 0, id);
            const sameLength = prev && prev.length === filteredOrder.length;
            if (sameLength && prev?.every((value, index) => value === filteredOrder[index])) {
                return prev;
            }
            return filteredOrder;
        });
        handleAutoScroll(event);
    };

    const handleStop = (id: string) => (_event: DraggableEvent, data: DraggableData) => {
        const preDragExtra = dragState ? 0 : getPreDragExtraOffset(id);
        const nextY = clampY(Math.round(data.y), blocks.length, preDragExtra);
        const normalizedY = Math.max(0, nextY - preDragExtra);
        const proposedIndex = Math.round(normalizedY / PIPELINE_BLOCK_SPACING);
        const finalOrder = getCurrentOrder(blocks, visualOrder);
        const finalIndex = finalOrder.indexOf(id);
        const targetIndex = finalIndex >= 0 ? finalIndex : proposedIndex;
        const currentIndex = blocks.findIndex((block) => block.id === id);

        if (targetIndex !== -1 && targetIndex !== currentIndex) {
            onMove(id, targetIndex);
        }

        if (hasTriggeredDragStartRef.current) {
            suppressNextClickRef.current = true;
        }
        setDragState(null);
        setVisualOrder(null);
        dragDistanceSinceStartRef.current = 0;
        hasTriggeredDragStartRef.current = false;
        stopAutoScroll();
    };

    useEffect(() => {
        if (!selectedBlockId || !hasParamsById.get(selectedBlockId)) {
            setSelectedBlockExtraHeight(0);
            return;
        }

        const selectedNode = blockRefs.current.get(selectedBlockId)?.current;
        if (!selectedNode) {
            setSelectedBlockExtraHeight(0);
            return;
        }

        const updateSelectedHeight = () => {
            const nextExtraHeight = Math.max(0, Math.ceil(selectedNode.offsetHeight - PIPELINE_BLOCK_SPACING));
            setSelectedBlockExtraHeight((prev) => (prev === nextExtraHeight ? prev : nextExtraHeight));
        };

        updateSelectedHeight();
        const observer = new ResizeObserver(updateSelectedHeight);
        observer.observe(selectedNode);

        return () => observer.disconnect();
    }, [hasParamsById, orderedBlockIds, selectedBlockId]);

    useEffect(() => {
        onSelectedBlockExtraHeightChange?.(selectedBlockExtraOffset);
    }, [onSelectedBlockExtraHeightChange, selectedBlockExtraOffset]);

    const computePosition = (blockId: string, index: number) => {
        if (dragState?.id === blockId) return dragState.y;
        const baseY = slotToY(index);
        if (selectedBlockIndex < 0 || selectedBlockExtraOffset <= 0) {
            return baseY;
        }
        if (index > selectedBlockIndex) {
            return baseY + selectedBlockExtraOffset;
        }
        return baseY;
    };

    const activeBounds = {
        top: 0,
        bottom: slotToY(Math.max(0, blocks.length - 1)) + selectedBlockExtraOffset,
    };
    const canDrag = blocks.length > 1;

    return (
        <>
            {orderedBlocks.map((block, index) => {
                const nodeRef = ensureRef(block.id);
                const isDragging = dragState?.id === block.id;
                const isSelected = selectedBlockId === block.id;
                const showInlineParameters = isSelected && Boolean(hasParamsById.get(block.id));
                const isDevelopmentBlock = DEVELOPMENT_BLOCK_TYPES.has(block.type);
                const blockDescription = blockMetadataById.get(block.id)?.description;

                const blockClassName = clsx('pipeline-block', {
                    'pipeline-block--selected': isSelected,
                    'pipeline-block--dragging': isDragging,
                    'pipeline-block--expanded': showInlineParameters,
                    'pipeline-block--disabled': !block.params.enabled,
                });

                const handleClassName = clsx('pipeline-drag-handle drag-handle', {
                    'pipeline-drag-handle--dragging': isDragging,
                });

                return (
                    <Draggable
                        key={block.id}
                        disabled={!canDrag}
                        axis="y"
                        bounds={activeBounds}
                        cancel=".pipeline-block-actions, .pipeline-block-actions *, .pipeline-block-info-button, .pipeline-block-info-button *, .pipeline-block-parameters, .pipeline-block-parameters *"
                        nodeRef={nodeRef as RefObject<HTMLElement | null>}
                        position={{ x: 0, y: computePosition(block.id, index) }}
                        onStart={handleStart}
                        onDrag={handleDrag(block.id)}
                        onStop={handleStop(block.id)}
                    >
                        <Box
                            className={blockClassName}
                            ref={nodeRef}
                            onClick={(event) => {
                                if (suppressNextClickRef.current) {
                                    suppressNextClickRef.current = false;
                                    return;
                                }
                                const target = event.target as HTMLElement;
                                if (target.closest('.pipeline-block-actions') || target.closest('.pipeline-block-parameters')) {
                                    return;
                                }
                                onSelect(block.id);
                            }}
                        >
                            <ChevronSelectorVertical
                                className={handleClassName}
                                role="button"
                                aria-label={`Drag ${block.name}`}
                                tabIndex={-1}
                                onMouseDown={(event) => event.preventDefault()}
                            ></ChevronSelectorVertical>

                            <Box className="pipeline-block-header">
                                <span className="pipeline-block-label">
                                    <Box
                                        component="span"
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'baseline',
                                            gap: 0.5,
                                            lineHeight: 1,
                                        }}
                                    >
                                        <span>{block.name}</span>
                                        {isDevelopmentBlock && <UMBetaBadge theme={theme.palette.mode === 'dark' ? 'dark' : 'light'} />}
                                    </Box>
                                    {blockDescription ? (
                                        <CustomTooltipOffsetProvider value={showInlineParameters}>
                                            <CustomTooltip side="right" title={blockDescription} customOffset={0}>
                                                <IconButton
                                                    className="pipeline-block-info-button"
                                                    aria-label={`More info about ${block.name}`}
                                                    size="small"
                                                    sx={{ color: 'inherit' }}
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                    }}
                                                >
                                                    <InfoCircle className="icon-size-small" />
                                                </IconButton>
                                            </CustomTooltip>
                                        </CustomTooltipOffsetProvider>
                                    ) : null}
                                </span>

                                <Box className="pipeline-block-actions">
                                    <IconButton
                                        className="pipeline-block-delete-button"
                                        data-testid={`block-delete-${block.type}`}
                                        sx={{ color: 'inherit' }}
                                        onClick={(e) => {
                                            e.stopPropagation();

                                            // 1️) Destroy DrawModule if attached to this block:
                                            PolygonDrawerManager.getInstance().destroyByKey(block.id);

                                            // 2) Delete the block from the pipeline:
                                            onDelete(block.id, e);
                                        }}
                                    >
                                        <Trash04 className="icon-size-medium" />
                                    </IconButton>

                                    <IconButton
                                        sx={{ color: 'inherit' }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onToggle(block.id, e);
                                        }}
                                    >
                                        {block.params.enabled ? (
                                            <Toggle01Right className="icon-size-medium" />
                                        ) : (
                                            <Toggle01Left className="icon-size-medium" />
                                        )}
                                    </IconButton>
                                </Box>
                            </Box>

                            {showInlineParameters && (
                                <CustomTooltipOffsetProvider value={true}>
                                    <Box className="pipeline-block-parameters">
                                        <PipelineParametersSection blockId={block.id} blockType={block.type} />
                                    </Box>
                                </CustomTooltipOffsetProvider>
                            )}
                        </Box>
                    </Draggable>
                );
            })}
        </>
    );
};

export default memo(PipelineBlockList);
